import { useMutation } from '@apollo/client';
import type { UpdateEntityMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateEntityDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { UpdateEntityTRPCInput } from './useUpdateEntityTRPC';
import { useUpdateEntityTRPC } from './useUpdateEntityTRPC';

export const useUpdateEntity = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation (legacy)
  const [updateEntityGraphQL, graphqlState] = useMutation(
    UpdateEntityDocument,
    {
      update: (cache) => {
        evictField(cache, 'entity');
        evictField(cache, 'entity_by_pk');
      },
    }
  );

  // tRPC mutation
  const trpcMutation = useUpdateEntityTRPC();

  const updateEntity = async (
    variables: UpdateEntityTRPCInput
  ): Promise<UpdateEntityMutation> => {
    if (trpcEnabled) {
      return trpcMutation.updateEntity(variables);
    }

    const result = await updateEntityGraphQL({
      variables: {
        Id: variables.Id,
        Name: variables.Name,
        Description: variables.Description,
        ParentId: variables.ParentId,
        Weight: variables.Weight,
        owners: variables.OwnerUserIds ?? [],
        ownerGroups: variables.OwnerGroupIds ?? [],
      },
    });
    if (!result.data) {
      throw new Error('Failed to update entity');
    }

    return result.data;
  };

  // Return appropriate state based on feature flag
  if (trpcEnabled) {
    return {
      updateEntity,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateEntity,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
